import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Sparkles, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import FileUpload from "../../components/FileUpload";
import DashboardLayout from "../../components/layout/DashboardLayout";
import api, { formatApiError } from "../../lib/api";

const fields = [
  ["business_name", "Business name *", "input"],
  ["business_description", "Business description", "textarea"],
  ["phone", "Phone", "input"],
  ["email", "Email", "input"],
  ["address", "Address", "input"],
  ["business_hours", "Business hours", "input"],
  ["services", "Services offered", "textarea"],
  ["service_areas", "Service areas", "input"],
  ["social_links", "Social media links", "input"],
  ["brand_colors", "Brand colors", "input"],
  ["competitor_websites", "Competitor websites", "input"],
  ["preferred_examples", "Preferred website examples", "input"],
  ["desired_action", "Desired customer action", "input"],
];

export default function Onboarding() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ project_id: projectId, photos: [] });
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const [contentType, setContentType] = useState("homepage");

  useEffect(() => {
    api.get(`/onboarding/${projectId}`).then((r) => {
      if (r.data) {
        setForm({ ...r.data, project_id: projectId, photos: r.data.photos || [] });
      }
    }).catch(() => {});
  }, [projectId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name) { toast.error("Business name is required"); return; }
    setLoading(true);
    try {
      await api.post("/onboarding", { ...form, project_id: projectId, photos: form.photos || [] });
      toast.success("Onboarding submitted! We'll start building.");
      navigate(`/portal/project/${projectId}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const generateCopy = async () => {
    if (!form.business_name) { toast.error("Add a business name first"); return; }
    setAiLoading(true); setAiText("");
    try {
      const { data } = await api.post("/content/generate", {
        content_type: contentType,
        business_name: form.business_name, industry: form.industry,
        business_description: form.business_description, services: form.services,
        notes: form.desired_action,
      });
      setAiText(data.content);
      toast.success("AI copy generated!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <DashboardLayout title="Project Onboarding" actions={<Button variant="outline" className="rounded-full" onClick={() => navigate("/portal")}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>}>
      <p className="text-[#948C79] -mt-4 mb-8 max-w-2xl">Tell us about your business so we can build the perfect website. The more detail, the better.</p>
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={submit} data-testid="onboarding-form" className="lg:col-span-2 grid sm:grid-cols-2 gap-5 bg-[#141414] rounded-2xl border border-white/10 p-8">
          {fields.map(([key, label, type]) => (
            <div key={key} className={`space-y-1.5 ${type === "textarea" ? "sm:col-span-2" : ""}`}>
              <Label className="text-sm text-[#D2CAB2]">{label}</Label>
              {type === "textarea"
                ? <Textarea data-testid={`onboarding-${key}`} value={form[key] || ""} onChange={set(key)} rows={3} />
                : <Input data-testid={`onboarding-${key}`} value={form[key] || ""} onChange={set(key)} />}
            </div>
          ))}
          <div className="sm:col-span-2 grid sm:grid-cols-2 gap-5">
            <FileUpload label="Business logo" testid="onboarding-logo-upload" value={form.logo_url || ""} onChange={(v) => setForm((f) => ({ ...f, logo_url: v }))} />
            <FileUpload label="Business photos" multiple testid="onboarding-photos-upload" value={form.photos || []} onChange={(v) => setForm((f) => ({ ...f, photos: v }))} />
          </div>
          <div className="sm:col-span-2">
            <Button data-testid="onboarding-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black h-12">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit onboarding"}
            </Button>
          </div>
        </form>

        <div className="space-y-4">
          <Card className="p-6 bg-black text-white">
            <div className="flex items-center gap-2 font-display font-semibold"><Sparkles className="h-5 w-5 text-[#E4C96A]" /> AI Content Studio</div>
            <p className="mt-2 text-sm text-[#7E7663]">Generate professional website content from your business details.</p>
            <select
              data-testid="ai-content-type"
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="mt-4 w-full rounded-lg bg-[#141414] border border-white/15 text-[#F3EDDC] text-sm px-3 py-2"
            >
              <option value="homepage">Homepage copy</option>
              <option value="about">About page</option>
              <option value="services">Service descriptions</option>
              <option value="faq">FAQ</option>
              <option value="cta">Call-to-action lines</option>
              <option value="meta">SEO meta title & description</option>
              <option value="blog">Blog post ideas</option>
              <option value="social">Social media captions</option>
              <option value="email">Email copy</option>
            </select>
            <Button data-testid="ai-generate-btn" onClick={generateCopy} disabled={aiLoading} className="mt-3 w-full rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black">
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate content"}
            </Button>
            {aiLoading && <p className="mt-2 text-xs text-[#7E7663]">Generating… this can take up to a minute.</p>}
          </Card>
          {aiText && (
            <Card className="p-5" data-testid="ai-suggestion">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#F3EDDC]"><CheckCircle2 className="h-4 w-4 text-green-500" /> Suggested copy</div>
              <pre className="mt-3 whitespace-pre-wrap text-sm text-[#D2CAB2] font-sans leading-relaxed">{aiText}</pre>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
